import React, { useLayoutEffect, useState, useRef, useEffect } from 'react';
import { Text } from '@components/typography';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import {
  ScalingInput,
  SearchForm,
  ErrorDetail,
  StyledFindButton,
  StyledFindIcon,
  StyledCloseButton,
  ActionButtons,
  FlexInputContainer,
  AvatarImageContainer,
  StyledCoupleAvatar,
  SearchFormContainer,
  NoDataDetailText,
  SuggestionsContainer,
  RightBoulderContainer,
  StyledSearchIcon,
} from './searchComponent.styles';
import { helpText, emptyInput, inputPlaceholder, fetchMsg } from '@data-ts/pages/find-an-event';
import {
  BoulderContainer,
  CountText,
  CoupleContainer,
  DateField,
  DetailField,
  LinksWrapper,
  ListContainer,
  ListItemWrapper,
  NameField,
  NoDataMsg,
  ProfileWrapper,
  ResponseWrapper,
  ResultList,
} from '@sections/find-an-event/searchResponse.styles';
import { findACoupleGQLQuery } from '@sections/find-an-event/findACoupleGQLQuery';
import queryString from 'query-string';
import SkeletonListData from '@sections/find-an-event/skeletonListData';
import shortid from 'shortid';
import { Flex } from '@components/Flex';
import { PlaceholderAvatar } from '@sections/find-an-event/PlaceholderAvatars/PlaceholderAvatar';
import { Box } from '@components/Box';
import { ReactComponent as ClearInputButton } from './clear.svg';
import { ReactComponent as LeftBoulder } from './Left_Art.svg';
import { ReactComponent as RightBoulder } from './Right_Art.svg';
import { CTALink } from '@components/CTALink';
import { EXT_APP_ROOT_URL } from '@utils/urlHelpers.util';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';
import { addRendition } from '@utils/photoRendition';

const analytics = require('../../../static/js/joy');

type Props = {
  name?: string | string[];
};

type EventData = {
  event: {
    fireBaseId: string;
    website: string;
    photo?: {
      url: string;
    };
    activeSession: {
      hasUnlocked: boolean;
      canAccessPage: boolean;
    };
    pageInfo: {
      disabled: boolean;
    };
    info: {
      ownerFullName: string;
      fianceeFullName?: string;
      // this date field can come back null.
      dateV0918?: {
        timestamp?: string;
      };
      // this date field can also come back null.
      tentativeDate?: {
        timestamp?: string;
      };
      finalizedDate?: {
        timestamp?: string;
      };
    };
  };
};

type ListItemProps = {
  ownerFullName: string;
  fianceeFullName?: string;
  eventDate: string;
  website: string;
  photoUrl: string | null;
  index: number;
  registryPageDisabled: boolean;
};

const FindACoupleResultListItem = (props: ListItemProps) => {
  const websiteClicked = (e: React.MouseEvent) => {
    e.preventDefault();
    analytics.segment.sendTelemetryEvent('newmarketing.findACoupleWebsiteClicked', { website: props.website });
    window.open(EXT_APP_ROOT_URL + '/' + props.website, '_blank');
  };

  return (
    <ListItemWrapper key={shortid.generate()}>
      <Flex>
        {props.photoUrl ? (
          <a target="blank" id={`${props.ownerFullName}-website`} onClick={websiteClicked} href={EXT_APP_ROOT_URL + '/' + props.website}>
            <AvatarImageContainer>
              <StyledCoupleAvatar src={addRendition({ url: props.photoUrl, renditionSize: 'avatar' })} alt={'couple avatar'} />
            </AvatarImageContainer>
          </a>
        ) : (
          <a target="blank" id={`${props.ownerFullName}-website`} href={EXT_APP_ROOT_URL + '/' + props.website}>
            <AvatarImageContainer>
              <PlaceholderAvatar avatarIndex={props.index % 6} />
            </AvatarImageContainer>
          </a>
        )}

        <ProfileWrapper onClick={websiteClicked}>
          <NameField tagName="span" variant="austinNewsDek24">
            {`${props.ownerFullName}`}
            {props.fianceeFullName ? (
              <span>
                {' '}
                & <span className="fiancee">{`${props.fianceeFullName}`}</span>
              </span>
            ) : null}
          </NameField>
        </ProfileWrapper>
      </Flex>
      <DetailField>
        <DateField tagName="span" variant="dek15">
          {props.eventDate}
        </DateField>
        <LinksWrapper>
          {props.website && (
            <>
              <CTALink
                href={EXT_APP_ROOT_URL + '/' + props.website}
                id={`${props.ownerFullName}-website`}
                target="_blank"
                typography={'featureHed17'}
                hasChevron={true}
                onClick={websiteClicked}
              >
                Website
              </CTALink>
              {!props.registryPageDisabled ? (
                <Box marginLeft={[4, 0]}>
                  <CTALink
                    href={EXT_APP_ROOT_URL + '/' + props.website + '/registry'}
                    id={`${props.ownerFullName}-registry`}
                    target="_blank"
                    typography={'featureHed17'}
                    hasChevron={true}
                  >
                    Registry
                  </CTALink>
                </Box>
              ) : null}
            </>
          )}
        </LinksWrapper>
      </DetailField>
    </ListItemWrapper>
  );
};

const SearchComponent = (props: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [inputValue, setInputValue] = useState(props.name || '');
  const [searchText, setSearchText] = useState('');
  const [error, setError] = useState('');
  const [searchResult, setSearchResult] = useState<EventData[] | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const isMobileMediaQuery = useThemeMediaQuery('below_sm');
  const [isMobile, setIsMobile] = useState(false);

  dayjs.extend(utc);
  dayjs.extend(timezone);

  let foundText = searchResult && searchResult.length && totalResults <= 50 ? `${searchResult.length} Events found` : '50+ Events found';
  let notFoundText = `We couldn’t find “${searchText}”`;
  const message = searchResult && searchResult.length ? foundText : notFoundText;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    if (e.target.value.length === 0) {
      setSearchResult(null);
      setShowSuggestions(false);
      setTotalResults(0);
      setInputValue(e.target.value);
    } else {
      setInputValue(e.target.value);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  let loadingMessage = fetchMsg;

  if (!loading) {
    loadingMessage = message;
  } else if (inputValue === '') {
    loadingMessage = '';
  }

  const handleSubmit = () => {
    setShowSuggestions(false);
    if (!inputValue) {
      setError(emptyInput);
      return;
    }

    analytics.segment.sendTelemetryEvent('newmarketing.findACoupleSearchClick', { label: inputValue.toString() });

    // Fetch search results
    setLoading(true);
    findACoupleGQLQuery(inputValue.toString()).then(response => {
      setSearchText(inputValue.toString());
      setLoading(true);

      let data = response.data;
      let results = data.getEventsBySearch?.results as EventData[];
      let totalCount: number = data.getEventsBySearch?.totalCount;
      analytics.segment.sendTelemetryEvent('newmarketing.findACoupleResultsFound', {
        label: totalCount + 'Found',
        value: totalCount,
        searchTerm: inputValue.toString(),
        numberOfResults: totalCount,
      });

      results = results.filter(
        result => result.event.info.ownerFullName && result.event.info.ownerFullName !== null && result.event.info.ownerFullName !== 'null'
      );

      if (results.length === 0) {
        setShowSuggestions(true);
      }

      setSearchResult(results);
      setTotalResults(totalCount);
      setLoading(false);
    });
  };

  useLayoutEffect(() => {
    const inputEl = inputRef.current;
    if (inputEl && !isMobileMediaQuery) {
      inputEl.focus();
    }
    setIsMobile(Boolean(isMobileMediaQuery));
  }, [inputRef, inputValue, isMobileMediaQuery]);

  useEffect(() => {
    const name = queryString.parse(typeof window === 'undefined' ? '' : window.location.search).name || '';
    if (name && name.length > 0) {
      setInputValue(name);
      setLoading(true);
      findACoupleGQLQuery(name.toString()).then(response => {
        let data = response.data;
        let results = data.getEventsBySearch?.results as EventData[];
        let totalCount: number = data.getEventsBySearch?.totalCount;

        results = results.filter(
          result =>
            result.event.info.ownerFullName && result.event.info.ownerFullName !== null && result.event.info.ownerFullName !== 'null'
        );

        setSearchResult(results);
        setTotalResults(totalCount);
        setLoading(false);
      });
    }
  }, []);

  return (
    <Box style={{ width: '100%' }}>
      <CoupleContainer>
        <SearchFormContainer>
          <SearchForm>
            <Text tagName="h1" variant="sectionEyeBrow24" color={inputValue ? 'neutra4' : 'neutra6'}>
              Find an Event
            </Text>
            <FlexInputContainer flexDirection="row" alignItems="center">
              {!isMobile && <StyledSearchIcon />}
              <ScalingInput
                ref={inputRef}
                value={inputValue}
                onChange={handleChange}
                placeholder={inputPlaceholder}
                type="text"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                variant="navSearch148"
                onKeyDown={handleKeyPress}
                required
              />
              {inputValue && (
                <ActionButtons>
                  <StyledCloseButton
                    id="find-couple-clear"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      setInputValue('');
                      setSearchResult(null);
                      setShowSuggestions(false);
                      setTotalResults(0);
                    }}
                  >
                    <ClearInputButton />
                  </StyledCloseButton>
                  <StyledFindButton id="find-couple-arrow" form="find-couple-input-form" onClick={handleSubmit}>
                    <StyledFindIcon src={cloudAssetUrl('nav/nav-right-arrow.svg')} alt="" />
                  </StyledFindButton>
                </ActionButtons>
              )}
            </FlexInputContainer>
            {!inputValue && (
              <Text tagName="p" variant="navParent15" color="neutra5">
                {helpText}
              </Text>
            )}
            {error && (
              <ErrorDetail tagName="p" variant="navParent15" color="red1">
                {error}
              </ErrorDetail>
            )}
          </SearchForm>
        </SearchFormContainer>
        <ResponseWrapper isReady={loading || searchResult !== null}>
          <ResultList>
            {(loading || searchResult) && (
              <CountText tagName="p" variant="featureHed17" color="red5" error={showSuggestions}>
                {loadingMessage}
              </CountText>
            )}

            {showSuggestions && (
              <SuggestionsContainer>
                <NoDataMsg tagName="p" variant="featureHed17" color="mono3">
                  Try using...
                </NoDataMsg>

                <NoDataDetailText tagName="p" variant="dek15" color="black">
                  <span role="img" aria-label="sparkle">
                    ✨
                  </span>{' '}
                  Both first and last names
                </NoDataDetailText>

                <NoDataDetailText tagName="p" variant="dek15" color="black">
                  <span role="img" aria-label="ring">
                    ✨
                  </span>{' '}
                  Proper names, like Rebekah for Bekah
                </NoDataDetailText>

                <NoDataDetailText tagName="p" variant="dek15" color="black">
                  <span role="img" aria-label="flower">
                    ✨
                  </span>{' '}
                  Nicknames, like Tom for Thomas
                </NoDataDetailText>

                <NoDataDetailText tagName="p" variant="dek15" color="black">
                  <span role="img" aria-label="flower">
                    ✨
                  </span>{' '}
                  Their exact event handle, such as “Carrie’s Registry”
                </NoDataDetailText>

                <NoDataDetailText tagName="p" variant="dek15" color="black">
                  Still having trouble? Note that some hosts may have opted to be hidden from search results. Please check your invite for
                  the link or event handle.
                </NoDataDetailText>
              </SuggestionsContainer>
            )}

            <ListContainer flexDirection={'column'}>
              <SkeletonListData ready={!loading}>
                {searchResult &&
                  searchResult.map((item: EventData, index) => {
                    const { ownerFullName, fianceeFullName, dateV0918 } = item.event.info;
                    const { photo } = item.event;
                    const { disabled } = item.event.pageInfo;
                    let eventDate = '';
                    if (dateV0918 && dateV0918.timestamp) {
                      eventDate = dayjs(dateV0918.timestamp.substring(0, 10)).format('MMMM D, YYYY');
                    }
                    const website = item.event.website;

                    return (
                      <FindACoupleResultListItem
                        key={shortid.generate()}
                        ownerFullName={ownerFullName}
                        fianceeFullName={fianceeFullName}
                        eventDate={eventDate}
                        website={website}
                        index={index}
                        photoUrl={photo && photo.url ? photo.url : null}
                        registryPageDisabled={disabled}
                      />
                    );
                  })}
              </SkeletonListData>
            </ListContainer>
          </ResultList>
        </ResponseWrapper>
      </CoupleContainer>
      {!loading && searchResult === null && (
        <BoulderContainer>
          <LeftBoulder />
          <RightBoulderContainer>
            <RightBoulder />
          </RightBoulderContainer>
        </BoulderContainer>
      )}
    </Box>
  );
};

export default SearchComponent;
